// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  selectClient,
  selectClientId,
} from '@shared/data-access/slices/clients/clients-selectors';
import { createDataProvider } from '@shared/ui-kit/providers';
import { useSelector } from 'react-redux';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import { useCallback, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useConst } from '@shared/ui-kit/hooks/use-const';
import { useTranslation } from 'react-i18next';
import { type CreateAdvisoryProposalsRequestBody } from '@shared/data-access/types/brokerage-types';
import { type UserProfileData } from '../components/user-profile/use-user-profile';
import { mapClientProfileToDisplay } from '../utils/map-client-profile-display';
import { resolveDisplayedClientStatus } from '../utils/resolve-displayed-client-status';

export const useClientProviderFactory = () => {
  const { t } = useTranslation();

  const { enqueueSnackbar } = useSnackbar();

  const clientId = useSelector(selectClientId);

  const selectedClient = useSelector(selectClient);

  const { data: profileData, ...clientProfileQuery } =
    advisoryApi.useGetClientProfileQuery(
      {
        client_id: clientId!,
      },
      {
        skip: clientId === null,
        refetchOnMountOrArgChange: true,
      },
    );

  const [notification, notificationResult] = advisoryApi.useNotificationMutation({
    fixedCacheKey: 'shared-aggregator-notification',
  });

  const [createAdvisoryProposal, createAdvisoryProposalResult] =
    advisoryApi.useCreateAdvisoryProposalMutation({
      fixedCacheKey: 'shared-aggregator-create-advisory-proposal',
    });

  const [notificationSent, setNotificationSent] = useState(
    notificationResult.isSuccess,
  );

  const clientData = useMemo(() => {
    return {
      ...selectedClient,
      status: resolveDisplayedClientStatus(profileData, selectedClient?.status),
      advisory_proposal: profileData?.advisory_proposal ?? null,
    };
  }, [profileData, selectedClient]);

  const profile = useMemo<UserProfileData>(() => {
    if (!profileData)
      return {
        name: '',
        first_name: '',
        second_name: '',
        first_surname: '',
        second_surname: '',
        email: '',
        document_number: '',
        phone_number: '',
        address: '',
        postal_code: '',
        city: '',
        country: '',
        birth_date: '',
      };

    return mapClientProfileToDisplay(profileData, { nameFallback: '-' });
  }, [profileData]);

  const statusMap = useConst(
    () =>
      new Map([
        [
          'active',
          {
            id: 'active',
            color: 'primary' as const,
            label: t('common.active', { ns: 'common' }) as string,
          },
        ],
        [
          'pending',
          {
            id: 'pending',
            color: 'warning' as const,
            label: t('common.pending', { ns: 'common' }) as string,
          },
        ],
        [
          'inactive',
          {
            id: 'inactive',
            color: 'error' as const,
            label: t('common.inactive', { ns: 'common' }) as string,
          },
        ],
        [
          'private',
          {
            id: 'private',
            color: 'default' as const,
            label: t('common.private', { ns: 'common' }) as string,
          },
        ],
      ]),
  );

  const onNotification = useCallback(() => {
    notification({
      client_id: clientId!,
      type: 'advisory_proposal',
    }).then(({ error }) => {
      if (error) {
        enqueueSnackbar(t('errors.notification-error', { ns: 'errors' }), {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        });
        return;
      }

      setNotificationSent(true);

      enqueueSnackbar(
        t('client-profile.notification-success-message', { ns: 'components' }),
        {
          variant: 'success',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        },
      );
    });
  }, [clientId, enqueueSnackbar, notification, t]);

  const onCreateAdvisoryProposal = useCallback(
    (
      payload: Omit<CreateAdvisoryProposalsRequestBody, 'client_id'>,
      onComplete?: () => void,
    ) => {
      createAdvisoryProposal({
        client_id: clientId!,
        ...payload,
      }).then(({ error }) => {
        if (error) {
          enqueueSnackbar(t('errors.advisory-creation-error', { ns: 'errors' }), {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });

          return;
        }

        enqueueSnackbar(
          t('client-profile.advisory-creation-success-message', {
            ns: 'components',
          }),
          {
            variant: 'success',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          },
        );

        onComplete?.();
      });
    },
    [clientId, enqueueSnackbar, createAdvisoryProposal, t],
  );

  return {
    client: clientData,
    onNotification,
    notificationSent,
    statusMap,
    onCreateAdvisoryProposal,
    createAdvisoryProposalResult,
    clientProfileQuery,
    profile,
  } as const;
};

export const { ClientDataContext, ClientProvider, useClientData, withClient } =
  createDataProvider({
    factory: useClientProviderFactory,
    name: 'Client',
  });
